import { z } from "zod";

// Catalog-muscle vocabulary — the slug set used by Exercise.primary +
// Exercise.secondary across the catalog, frozen here so the volume
// aggregation (slice #4) speaks one vocabulary on every render surface
// (Logger live totals, post-session card, share PNG, Progress panels).
//
// Distinct from the bodymap `MUSCLE_SLUGS` (~15 anatomical regions) in
// `bodymap.ts`: that vocabulary drives the pain picker (regions a user
// can mark as sore), this one drives volume-by-muscle (catalog tags an
// exercise targets). Different domain, different cardinality, no
// mapping in the volume path.
//
// Locked at 19 slugs after the 2026-05-28 catalog audit
// (next/muscle-catalog-audit.md). Two typos in the dev catalog (`chest`,
// `upper-back`, 5 rows total) get remapped in Phase 3 alongside the
// muscleContrib enrichment write — keeping all DDB touches in one
// coordinated session.
export const CATALOG_MUSCLE_SLUGS = [
  // Lower body
  "quadriceps",
  "hamstrings",
  "glutes",
  "adductors",
  "abductors",
  "calves",
  // Trunk
  "core",
  "obliques",
  "erectors",
  // Upper-body push
  "pectoralis",
  "anterior-delts",
  "lateral-delts",
  "triceps",
  // Upper-body pull
  "lats",
  "rhomboids",
  "rear-delts",
  "traps",
  "biceps",
  // Distal
  "forearms",
] as const;

export const CatalogMuscleSlug = z.enum(CATALOG_MUSCLE_SLUGS);
export type CatalogMuscleSlug = z.infer<typeof CatalogMuscleSlug>;

// Per-exercise recruitment map: each muscle the lift trains gets a
// contribution in [0, 1]. NOT normalised — a heavy compound like a
// band-squat legitimately contributes 0.7 to quadriceps and 0.6 to
// glutes simultaneously. The aggregation rule (slice #4 Phase 4) is
//   volumeByMuscle[slug] += setVolumeKg * muscleContrib[slug] / Σ muscleContrib
// (tonnage is split across muscles, #529; hard-set credit uses the raw weights)
// applied identically on mobile + server so Logger totals, post-session
// card, share PNG and Progress all show the same number.
//
// `partialRecord` keeps the map sparse — unset muscles read as 0
// contribution (the lift doesn't train them), no double-counting risk.
export const MuscleContrib = z.partialRecord(
  CatalogMuscleSlug,
  z.number().min(0).max(1),
);
export type MuscleContrib = z.infer<typeof MuscleContrib>;

// i18n key helper. Phase 5 ships the 19 `muscle.<slug>` translations in
// 5 locales (en/it/fr/de/ru); consumers MUST use this helper instead of
// open-coding `muscle.${slug}` so a typo at the call site is a compile
// error rather than a defaultValue fallback to the raw slug.
export const muscleKey = (slug: CatalogMuscleSlug): string =>
  `muscle.${slug}`;

// Short uppercase tags used ONLY for SessionResult.sessionLabel — a
// derived "QUAD · POSTERIOR"-style header stored on the row at compute
// time. Stored strings are not localised at render time (the field is
// already a finished label), so this stays in EN by design, mirroring
// the previous bodymap MUSCLE_LABEL behaviour. Renderers that show
// muscle names to the user should use `t(muscleKey(slug))` instead.
const SHORT_LABEL_OVERRIDES: Partial<Record<CatalogMuscleSlug, string>> = {
  "anterior-delts": "FRONT DELT",
  "lateral-delts": "SIDE DELT",
  "rear-delts": "REAR DELT",
};

export const muscleShortLabel = (slug: CatalogMuscleSlug): string =>
  SHORT_LABEL_OVERRIDES[slug] ?? slug.toUpperCase();
